// Shared domain model for the app, the ingestion scripts, and the tests.
// Keep this file free of Firebase imports so it can run anywhere.

export const TOPICS = [
  "ai_agents",
  "data_engineering",
  "analytics_bi",
  "governance",
  "platform",
  "business_impact",
] as const;
export type Topic = (typeof TOPICS)[number];

export const TOPIC_LABELS: Record<Topic, string> = {
  ai_agents: "AI & Agents",
  data_engineering: "Data Engineering",
  analytics_bi: "Analytics & BI",
  governance: "Governance",
  platform: "Platform",
  business_impact: "Business Impact",
};

/** Audiences a story is tagged for. "everyone" is a selector value, not a tag. */
export const AUDIENCE_TAGS = ["business", "technical", "new_to_databricks"] as const;
export type AudienceTag = (typeof AUDIENCE_TAGS)[number];
export const AUDIENCES = ["everyone", ...AUDIENCE_TAGS] as const;
export type Audience = (typeof AUDIENCES)[number];

export const AUDIENCE_LABELS: Record<Audience, string> = {
  everyone: "Everyone",
  business: "Business",
  technical: "Technical",
  new_to_databricks: "New to Databricks",
};

export const LENSES = ["plainEnglish", "businessImpact", "technicalDetail"] as const;
export type Lens = (typeof LENSES)[number];
export const LENS_LABELS: Record<Lens, string> = {
  plainEnglish: "Plain English",
  businessImpact: "Business impact",
  technicalDetail: "Technical detail",
};

export const CONTENT_TYPES = ["blog", "announcement", "release_note"] as const;
export type ContentType = (typeof CONTENT_TYPES)[number];
export const CONTENT_TYPE_LABELS: Record<ContentType, string> = {
  blog: "Blog",
  announcement: "Announcement",
  release_note: "Release note",
};

export const SOURCE_TYPES = ["blog", "announcement", "release_notes"] as const;
export type SourceType = (typeof SOURCE_TYPES)[number];

export const CLOUDS = ["aws", "azure", "gcp", "cross_cloud", "unknown"] as const;
export type Cloud = (typeof CLOUDS)[number];
export const CLOUD_LABELS: Record<Cloud, string> = {
  aws: "AWS",
  azure: "Azure",
  gcp: "Google Cloud",
  cross_cloud: "Cross-cloud",
  unknown: "Unknown",
};

export const RELEASE_STATUSES = [
  "announced",
  "private_preview",
  "public_preview",
  "beta",
  "ga",
  "deprecated",
  "unknown",
] as const;
export type ReleaseStatus = (typeof RELEASE_STATUSES)[number];
export const RELEASE_STATUS_LABELS: Record<ReleaseStatus, string> = {
  announced: "Announced",
  private_preview: "Private Preview",
  public_preview: "Public Preview",
  beta: "Beta",
  ga: "Generally Available",
  deprecated: "Deprecated",
  unknown: "Status unknown",
};

export type StoryStatus = "draft" | "published" | "archived";
/**
 * "source_excerpt": all lenses show the official excerpt because no editor has
 * written summaries yet (see `publish:excerpts`). Labeled "Official excerpt".
 */
export type SummaryProvenance = "editorial" | "ai_assisted" | "sample" | "source_excerpt";

import type { DocsLabel } from "./flags.ts";
import type { Role } from "./roles.ts";

export interface SourceLink {
  label: string;
  url: string;
  type: "source" | "docs" | "release_note" | "blog" | "announcement";
}

export interface Summaries {
  plainEnglish: string;
  businessImpact: string;
  technicalDetail: string;
}

/**
 * Client-side story shape. Firestore Timestamps are converted to epoch
 * milliseconds at the repository boundary so UI code never touches SDK types.
 */
export interface Story {
  id: string;
  title: string;
  canonicalUrl: string;
  sourceId: string;
  sourceName: string;
  contentType: ContentType;
  primaryTopic: Topic;
  topicTags: string[];
  audiences: AudienceTag[];
  publishedAt: number | null;
  sourceUpdatedAt: number | null;
  /** publishedAt ?? firstSeenAt — the single field used for ordering/cursors. */
  sortAt: number;
  summaries: Summaries;
  whyItMatters: string;
  /**
   * Who wrote whyItMatters. "ai_generated" lines are written by a model from the
   * story's own title and official text (see `why:generate`) and are always
   * labeled AI-generated. Absent = editorial (or none for excerpt-only stories).
   */
  whyProvenance?: "editorial" | "ai_generated";
  /** Model endpoint that wrote an AI-generated whyItMatters. */
  whyModel?: string | null;
  estimatedReadMinutes: number;
  releaseStatus: ReleaseStatus;
  clouds: Cloud[];
  availabilityNotes: string | null;
  availabilityVerifiedAt: number | null;
  availabilitySourceUrl: string | null;
  sourceLinks: SourceLink[];
  summaryProvenance: SummaryProvenance;
  isSample: boolean;
  /** Platform-map product IDs (shared/products.ts). Editorial. */
  products: string[];
  /** Status stated in an official release-note title (shared/flags.ts). Source metadata. */
  docsLabel: DocsLabel | null;
  /** Clouds whose official release notes list this entry. Not an availability claim. */
  docsClouds: Cloud[];
  /** Roles (shared/roles.ts), derived from products/topic and kept in sync by map:rebuild. */
  roles?: Role[];
}

/** A story's release status / cloud labels are only shown when verified. */
/** How to show "Why it matters": editor-written, AI-generated, or not at all. */
export function whyKind(s: Pick<Story, "whyItMatters" | "whyProvenance" | "summaryProvenance">): "editorial" | "ai" | null {
  if (!s.whyItMatters.trim()) return null;
  if (s.whyProvenance === "ai_generated") return "ai";
  // Excerpt-only stories carry a placeholder, not an editor's line.
  return s.summaryProvenance === "source_excerpt" ? null : "editorial";
}

export function hasVerifiedAvailability(s: Story): boolean {
  return (
    !s.isSample &&
    s.availabilityVerifiedAt !== null &&
    typeof s.availabilitySourceUrl === "string" &&
    s.availabilitySourceUrl.startsWith("https://")
  );
}

// ---- Reading Basket ---------------------------------------------------------

export interface SavedStory {
  storyId: string;
  savedAt: number;
  order: number;
  isRead: boolean;
  readAt: number | null;
  /** 0..1. 0.5 = opened in a reading session, 1 = finished. */
  readingProgress: number | null;
}

export interface Preferences {
  audience: Audience;
  role: Role | "all";
  lens: Lens;
  followedTopics: Topic[];
  preferredClouds: Cloud[];
}

export const DEFAULT_PREFERENCES: Preferences = {
  audience: "everyone",
  role: "all",
  lens: "plainEnglish",
  followedTopics: [],
  preferredClouds: [],
};

/** Upper bounds mirrored in firestore.rules. Keep both in sync. */
export const LIMITS = {
  maxSavedStories: 200,
  maxOrder: 10_000,
  storyIdPattern: /^[a-z0-9][a-z0-9_-]{0,127}$/,
  pageSize: 15,
  searchLoadedCap: 60,
} as const;
